<template>
  <v-app>

    <!-- ===================== MODAL ===================== -->
    <v-dialog v-model="localDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="text-h6 bg-blue-grey-lighten-4">
          <span>{{ isEntryMode ? "Add Sewing Line" : "Update Sewing Line" }}</span>
        </v-card-title>

        <v-card-text>
          <v-form @submit.prevent="submit" ref="formRef">
            <v-row>

              <!-- Target Per Hour -->
              <v-col cols="12">
                <v-text-field
                  v-model="target_per_hour.value.value"
                  type="number"
                  label="Target Per Hour"
                  variant="outlined"
                  density="compact"
                  :error-messages="target_per_hour.errorMessage.value"
                  clearable
                >
                  <template #label>
                    Target Per Hour <span class="text-red-accent-4">*</span>
                  </template>
                </v-text-field>
              </v-col>

              <!-- Status Checkbox -->
              <v-col cols="12">
                <v-checkbox
                    label="Active"
                    v-model="status.value.value"
                    :error-messages="status.errorMessage.value"
                    density="compact"
                    hide-details
                    single-line
                    color="primary"
                ></v-checkbox>
              </v-col>

              <input type="hidden" v-model="sewing_line_id.value.value" />
            </v-row>

            <v-divider></v-divider>

            <v-card-actions class="justify-end">
              <v-btn variant="flat" color="red" @click="closeDialog">Close</v-btn>
              <v-btn class="ms-2" variant="flat" color="primary" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-card-actions>

          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===================== PAGE CONTENT ===================== -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">

          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title class="bg-blue-grey-lighten-4">
                Production Sewing Line List
              </v-expansion-panel-title>

              <v-expansion-panel-text>

                <!-- Search + Add Button -->
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      variant="outlined"
                      density="compact"
                      hide-details
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="3" offset-md="5" class="text-right">
                    <v-btn
                      color="primary"
                      variant="tonal"
                      prepend-icon="mdi-plus"
                      @click="openCreate"
                      rounded="xl"
                    >
                      Add Sewing Line
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- TABLE -->
                <v-data-table
                  :headers="headers"
                  :items="lines"
                  :loading="loading"
                  :search="search"
                  density="compact"
                >
                  <template v-slot:item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <template v-slot:item.status="{ item }">
                    <v-chip size="small" :color="item.status == 1 ? 'success' : 'red'">
                      {{ item.status == 1 ? "Active" : "Inactive" }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-icon color="primary" @click="openEdit(item)">mdi-pencil-outline</v-icon>
                  </template>
                </v-data-table>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

        </v-col>
      </v-row>
    </v-container>

  </v-app>
</template>

<script>
import { ref } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

export default {
  data() {
    return {
      panel: [0],
      localDialog: false,
      search: "",
      isEntryMode: true,
      loading: false,

      headers: [
        { title: "#SL", key: "index", align: "center" },
        { title: "Code", key: "code" },
        { title: "Target / Hr", key: "target_per_hour" },
        { title: "Status", key: "status", align: "center" },
        { title: "Actions", key: "actions", align: "center", sortable: false },
      ],
    };
  },

  setup() {
    const store = useStore();
    const localDialog = ref(false);

    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        target_per_hour: yup.number().required().min(1).label("Target Per Hour"),
        status: yup.boolean().required().label("Status"),
      }),
    });

    const target_per_hour = useField("target_per_hour");
    const status = useField("status");
    const sewing_line_id = useField("sewing_line_id");

    const closeDialog = () => {
      localDialog.value = false;
      resetForm();
    };

    const submit = handleSubmit((values) => {
      const payload = {
        target_per_hour: values.target_per_hour,
        status: values.status ? 1 : 0,
      };

      const id = values.sewing_line_id;

      if (id) {
        store
          .dispatch("updateProductionSewingLine", { id, data: payload })
          .then(() => {
            toast.success("Updated Successfully!");
            resetForm();
            closeDialog();
            store.dispatch("getProductionSewingLineList");
          })
          .catch((err) => {
            toast.error(err.response?.data?.message || "Something went wrong");
          });
      } else {
        store
          .dispatch("saveProductionSewingLine", payload)
          .then(() => {
            toast.success("Created Successfully!");
            resetForm();
            closeDialog();
            store.dispatch("getProductionSewingLineList");
          })
          .catch((err) => {
            toast.error(err.response?.data?.message || "Something went wrong");
          });
      }
    });

    return {
      target_per_hour,
      status,
      sewing_line_id,
      submit,
      resetForm,
      localDialog,
      closeDialog,
    };
  },

  computed: {
    lines() {
      return this.$store.getters.getProductionSewingLineList;
    },
  },

  methods: {
    loadList() {
      this.loading = true;
      this.$store.dispatch("getProductionSewingLineList").finally(() => {
        this.loading = false;
      });
    },

    openCreate() {
      this.isEntryMode = true;
      this.localDialog = true;
      this.resetForm();
      this.status.setValue(true);
    },

    openEdit(item) {
      this.isEntryMode = false;
      this.localDialog = true;
      this.target_per_hour.setValue(item.target_per_hour);
      this.status.setValue(item.status == 1);
      this.sewing_line_id.setValue(item.id);
    },
  },

  created() {
    this.loadList();
  },
};
</script>
